import React, { useCallback } from "react";

// LlamaCloud supports 130+ formats; include common document types
const ACCEPT =
  ".pdf,.doc,.docx,.txt,.md,.rtf,.ppt,.pptx,.xlsx,.xls,.csv,.html,.htm,.epub,.xml,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.svg";

interface DocumentUploadZoneProps {
  onUpload: (files: File[]) => void | Promise<void>;
  isUploading: boolean;
  inputId?: string;
}

function getFilesFromList(fileList: FileList | null): File[] {
  if (!fileList || fileList.length === 0) return [];
  return Array.from(fileList);
}

export function DocumentUploadZone({
  onUpload,
  isUploading,
  inputId = "document-upload",
}: DocumentUploadZoneProps) {
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = getFilesFromList(e.dataTransfer.files);
      if (files.length > 0 && !isUploading) {
        onUpload(files);
      }
    },
    [onUpload, isUploading]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = getFilesFromList(e.target.files);
      if (files.length > 0 && !isUploading) {
        onUpload(files);
      }
      e.target.value = "";
    },
    [onUpload, isUploading]
  );

  return (
    <label
      htmlFor={inputId}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      className={`
        block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
        transition-colors
        ${isUploading ? "opacity-50 cursor-not-allowed" : "hover:border-primary/50"}
      `}
    >
      <input
        type="file"
        accept={ACCEPT}
        multiple
        onChange={handleChange}
        disabled={isUploading}
        className="hidden"
        id={inputId}
      />
      {isUploading ? (
        <span className="text-sm text-muted-foreground">Loading…</span>
      ) : (
        <p className="text-base font-medium mb-1">
          Drag and drop or click to upload
        </p>
      )}
    </label>
  );
}
